import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import '../App.css'; // Add your styling if needed

function CustomScheduleBuilder({ schedule, onChange }) {
    const {
        daysOfWeek = [],
        fromHour = 7,
        fromMinute = 30,
        untilHour = 9,
        untilMinute = 0,
        excludedDates = [],
    } = schedule || {};

    const navigate = useNavigate();
    const dayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    const handleToggleDay = (dayIndex) => {
        let newDays;
        if (daysOfWeek.includes(dayIndex)) {
            newDays = daysOfWeek.filter((d) => d !== dayIndex);
        } else {
            newDays = [...daysOfWeek, dayIndex];
        }
        onChange({ ...schedule, daysOfWeek: newDays });
    };

    const handleTimeChange = (e) => {
        const { name, value } = e.target; // e.g., name="fromHour", value="7"
        onChange({ ...schedule, [name]: Number(value) });
    };

    const handleAddExcludeDate = (e) => {
        const dateStr = e.target.value; // 'YYYY-MM-DD'
        if (!dateStr) return;
        if (!excludedDates.includes(dateStr)) {
            onChange({
                ...schedule,
                excludedDates: [...excludedDates, dateStr],
            });
        }
        e.target.value = '';
    };

    const handleRemoveExcludedDate = (dateStr) => {
        onChange({
            ...schedule,
            excludedDates: excludedDates.filter((d) => d !== dateStr),
        });
    };

    return (
        <div style={{ padding: '10px', border: '1px solid #ccc', marginTop: '10px' }}>
            <h4>Schedule Settings</h4>

            {/* Days of Week */}
            <div style={{ marginBottom: '10px' }}>
                <strong>Days of the Week:</strong>

                <div
                    style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '10px',
                        marginTop: '5px',
                        alignItems: 'center'
                    }}
                >
                    {dayLabels.map((label, i) => (
                        <label
                            key={label}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center'
                            }}
                        >
                            <input
                                type="checkbox"
                                checked={daysOfWeek.includes(i)}
                                onChange={() => handleToggleDay(i)}
                                style={{ marginRight: '4px' }}
                            />
                            {label}
                        </label>
                    ))}
                </div>
            </div>

            {/* Time Range */}
            <div style={{ marginBottom: '10px' }}>
                <strong>Time Range:</strong>
                <div style={{ marginLeft: '10px', marginTop: '5px' }}>
                    {/* From Time */}
                    <label>From: </label>
                    <select name="fromHour" value={fromHour} onChange={handleTimeChange}>
                        {Array.from({ length: 24 }).map((_, idx) => (
                            <option key={idx} value={idx}>
                                {String(idx).padStart(2, '0')}
                            </option>
                        ))}
                    </select>
                    :
                    <select name="fromMinute" value={fromMinute} onChange={handleTimeChange}>
                        {Array.from({ length: 60 }).map((_, idx) => (
                            <option key={idx} value={idx}>
                                {String(idx).padStart(2, '0')}
                            </option>
                        ))}
                    </select>

                    {/* Until Time */}
                    <label style={{ marginLeft: '15px' }}>Until: </label>
                    <select name="untilHour" value={untilHour} onChange={handleTimeChange}>
                        {Array.from({ length: 24 }).map((_, idx) => (
                            <option key={idx} value={idx}>
                                {String(idx).padStart(2, '0')}
                            </option>
                        ))}
                    </select>
                    :
                    <select name="untilMinute" value={untilMinute} onChange={handleTimeChange}>
                        {Array.from({ length: 60 }).map((_, idx) => (
                            <option key={idx} value={idx}>
                                {String(idx).padStart(2, '0')}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Excluded Dates */}
            <div>
                <strong>Excluded Dates (holidays, etc.):</strong>
                <div style={{ marginTop: '5px' }}>
                    <input type="date" onChange={handleAddExcludeDate} />
                </div>
                {excludedDates.length > 0 && (
                    <ul style={{ marginTop: '5px' }}>
                        {excludedDates.map((date) => (
                            <li key={date} style={{ marginBottom: '5px' }}>
                                {date}{' '}
                                <button
                                    type="button"
                                    onClick={() => handleRemoveExcludedDate(date)}
                                    style={{ marginLeft: '5px', cursor: 'pointer' }}
                                >
                                    Remove
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

function AddScenarioPage() {
    const [scenarioNameToLoad, setScenarioNameToLoad] = useState('');
    const [name, setName] = useState(`Scenario_${Date.now()}`);
    const [nameError, setNameError] = useState('');
    const [checkingName, setCheckingName] = useState(false);
    const [scenarioExists, setScenarioExists] = useState(false);
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const scenarioIdParam = query.get('scenarioId');
    const scenarioName = query.get('scenarioName');
    const { workspaceName } = useParams();
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

    useEffect(() => {
        if (scenarioName !== null && scenarioName !== "undefined") {
            setName(scenarioName);
            setScenarioNameToLoad(scenarioName);
        }
    }, [scenarioName]);

    useEffect(() => {
        if (scenarioNameToLoad) {
            handleLoadScenario();
        }
    }, [scenarioNameToLoad]);

    const [isActive, setIsActive] = useState(true);
    const [triggerConditions, setTriggerConditions] = useState([
        { type: 'sensor', name: '', operator: '==', value: 0, logicOp: 'AND' },
    ]);
    const [actions, setActions] = useState([{ type: 'actuator', name: '', command: '' }]);
    const [actionErrors, setActionErrors] = useState([]);
    const [sensorDevices, setSensorDevices] = useState([]);
    const [actuatorDevices, setActuatorDevices] = useState([]);

    useEffect(() => {
        const fetchSensorDevices = async () => {
            try {
                const token = localStorage.getItem('token');
                if (!token) throw new Error('No token found, user is not authenticated');
                const response = await axios.get(`${API_URL}/api/scenarios/${workspaceName}/sensors`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                
                setSensorDevices(response.data || []);
            } catch (err) {
                console.error('Error fetching sensor devices:', err);
            }
        };

        const fetchActuatorDevices = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(`${API_URL}/api/scenarios/${workspaceName}/actuators`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setActuatorDevices(response.data || []);
            } catch (err) {
                console.error('Error fetching actuator devices:', err);
            }
        };
        fetchSensorDevices();
        fetchActuatorDevices();
    }, []);

    const handleLoadScenario = async () => {
        if (!scenarioNameToLoad.trim()) {
            alert('Please type a scenario name to load.');
            return;
        }
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${API_URL}/api/scenarios/${workspaceName}?scenarioName=${encodeURIComponent(scenarioNameToLoad)}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            const scenario = response.data;
            if (!scenario || !scenario.scenarioName) {
                alert(`No scenario found with name "${scenarioNameToLoad}".`);
                return;
            }

            const newTC = [];
            (scenario.conditions || []).forEach((t) => {
                if (t.type === 'schedule') {
                    newTC.push({
                        type: 'schedule',
                        schedule: {
                            daysOfWeek: t.schedule?.daysOfWeek || [],
                            fromHour: t.schedule?.fromHour ?? 7,
                            fromMinute: t.schedule?.fromMinute ?? 30,
                            untilHour: t.schedule?.untilHour ?? 9,
                            untilMinute: t.schedule?.untilMinute ?? 0,
                            excludedDates: t.schedule?.excludedDates || []
                        },
                        name: '', // not used
                        operator: '==',
                        value: t.value,
                        logicOp: 'AND',
                    });
                } else if (t.type === 'manual') {
                    newTC.push({
                        type: 'manual',
                        name: '',
                        operator: '==',
                        value: t.value,
                        logicOp: 'AND',
                    });
                } else if (t.type === 'sensor') {
                    const hasSchedule =
                        t.schedule &&
                        (t.schedule.daysOfWeek?.length > 0 ||
                            t.schedule.excludedDates?.length > 0);

                    newTC.push({
                        type: 'sensor',
                        name: t.name || '',
                        operator: '==',
                        value: t.value,
                        logicOp: 'AND',
                        useSensorSchedule: !!hasSchedule,
                        schedule: hasSchedule
                            ? {
                                daysOfWeek: t.schedule.daysOfWeek || [],
                                fromHour: t.schedule.fromHour ?? 7,
                                fromMinute: t.schedule.fromMinute ?? 30,
                                untilHour: t.schedule.untilHour ?? 9,
                                untilMinute: t.schedule.untilMinute ?? 0,
                                excludedDates: t.schedule.excludedDates || []
                            }
                            : undefined
                    });
                }
            });

            const fixedActions = (scenario.actions || []).map((act) => {
                const lowerType = act.type ? act.type.toLowerCase() : 'actuator';
                return {
                    type: ['actuator', 'alarm', 'message'].includes(lowerType)
                        ? lowerType
                        : 'actuator',
                    name: act.name || '',
                    command: act.command || '',
                };
            });

            setName(scenario.scenarioName);
            setIsActive(scenario.isActive !== false);
            setTriggerConditions(newTC);
            setActions(fixedActions);
            setActionErrors(fixedActions.map(() => ({ name: false, command: false })));

        } catch (err) {
            console.error('Error loading scenario by name:', err);
            alert('Failed to load scenario.');
        }
    };

    const checkScenarioName = async (scenarioName) => {
        if (!scenarioName) {
            setNameError('Name cannot be empty.');
            setScenarioExists(false);
            return false;
        }
        try {
            setCheckingName(true);
            setNameError('');
            const token = localStorage.getItem('token');

            const response = await axios.get(
                `${API_URL}/api/scenarios/${workspaceName}/checkName?name=${encodeURIComponent(scenarioName)}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.exists) {
                setNameError(`Сценарий "${scenarioName}" уже существует`);
                setScenarioExists(true);
                return false;
            } else {
                setNameError('');
                setScenarioExists(false);
                return true;
            }
        } catch (err) {
            console.error('Error checking scenario name', err);
            setNameError('Failed to check scenario name');
            setScenarioExists(false);
            return false;
        } finally {
            setCheckingName(false);
        }
    };

    const handleNameBlur = async () => {
        if (name.trim()) {
            await checkScenarioName(name.trim());
        }
    };

    // ======== TRIGGERS & CONDITIONS ========
    const handleAddTriggerCondition = () => {
        setTriggerConditions([
            ...triggerConditions,
            { type: 'sensor', name: '', operator: '==', value: 0, logicOp: 'AND' },
        ]);
    };

    const handleRemoveTriggerCondition = (index) => {
        const newArr = [...triggerConditions];
        newArr.splice(index, 1);
        setTriggerConditions(newArr);
    };

    const handleTCChange = (index, field, value) => {
        const newArr = [...triggerConditions];
        newArr[index][field] = value;
        setTriggerConditions(newArr);
    };

    // We'll handle schedule changes for each item:
    const handleScheduleChange = (index, newSchedule) => {
        const newArr = [...triggerConditions];
        newArr[index].schedule = newSchedule;
        setTriggerConditions(newArr);
    };

    // ======== ACTIONS ========
    const handleAddAction = () => {
        setActions([...actions, { type: 'actuator', name: '', command: '' }]);
        setActionErrors([...actionErrors, { name: false, command: false }]);
    };

    const handleRemoveAction = (index) => {
        const newActions = [...actions];
        newActions.splice(index, 1);
        setActions(newActions);

        const newErrors = [...actionErrors];
        newErrors.splice(index, 1);
        setActionErrors(newErrors);
    };

    const handleActionChange = (index, field, value) => {
        if (value == "включить") value = 'on';
        if (value == "выключить") value = 'off';
        const newActions = [...actions];
        newActions[index][field] = value;
        setActions(newActions);

        const newErrors = [...actionErrors];
        if (newErrors[index]) {
            newErrors[index][field] = false;
        }
        setActionErrors(newErrors);
    };

    // ======== BUILD & SAVE ========
    const buildScenarioObject = () => {
        //const triggersToSave = [];
        const conditionsToSave = [];

        triggerConditions.forEach((item) => {
            if (item.type === 'sensor') {// Sensor = trigger + condition
                //const sensorTrigger = {
                //    type: 'sensor',
                //    name: item.name,
                //};
                //if (item.useSensorSchedule) {
                //    conditionsToSave.schedule = item.schedule || {
                //        daysOfWeek: [],
                //        fromHour: 0,
                //        fromMinute: 0,
                //        untilHour: 24,
                //        untilMinute: 0,
                //        excludedDates: []
                //    };
                //}
                conditionsToSave.push({
                    type: item.type,
                    name: item.name,
                    operator: item.operator,
                    value: item.value,
                    logicOp: item.logicOp,
                    schedule: item.schedule || {
                                daysOfWeek: [],
                                fromHour: 7,
                                fromMinute: 30,
                                untilHour: 9,
                                untilMinute: 0,
                                excludedDates: []
                    }
                });
                //conditionsToSave.push(sensorTrigger);

            } else if (item.type === 'manual') {
                conditionsToSave.push({
                    type: 'manual',
                    name: 'ManualTrigger',
                });
            } else if (item.type === 'schedule') {
                conditionsToSave.push({
                    type: 'schedule',
                    logicOp: item.logicOp,
                    schedule: item.schedule,
                });
            }
            //else if (item.type === 'scheduleCondition') { //purely schedule-based condition
            //    conditionsToSave.push({
            //        type: 'schedule', // We'll store as 'schedule' in the DB
            //        name: '',         // Not needed for pure schedule
            //        operator: '',     // Not needed
            //        value: '',        // Not needed
            //        logicOp: item.logicOp || 'AND',
            //        schedule: item.schedule || {
            //            daysOfWeek: [],
            //            fromHour: 7,
            //            fromMinute: 30,
            //            untilHour: 9,
            //            untilMinute: 0,
            //            excludedDates: []
            //        }
            //    });
            //}
        });

        // Validate conditions (e.g., sensor fields)
        for (const cond of conditionsToSave) {
            if (cond.type === 'sensor') {
                if (!cond.name || cond.value == null) {
                    alert('Please complete all sensor condition fields.');
                    return null;
                }
            }
            if (cond.type === 'schedule') {
                if (!cond.schedule) {
                    alert('Please set a schedule for schedule condition.');
                    return null;
                }
            }
        }

        // Validate triggers
        //for (const tr of triggersToSave) {
        //    if (tr.type === 'schedule' && !tr.schedule) {
        //        alert('Please set a schedule for schedule triggers.');
        //        return null;
        //    }
        //}

        // Validate actions
        let newActionErrors = actions.map(() => ({ name: false, command: false }));
        let hasActionError = false;
        actions.forEach((action, idx) => {
            if (!action.name) {
                newActionErrors[idx].name = true;
                hasActionError = true;
            }
            if (!action.command) {
                newActionErrors[idx].command = true;
                hasActionError = true;
            }
        });
        setActionErrors(newActionErrors);

        if (hasActionError) {
            alert('Please complete all action fields.');
            return null;
        }

        const scenarioName = name.trim();
        const newScenario = {
            workspaceName: workspaceName,
            scenarioName: scenarioName,
            isActive,
            //triggers: triggersToSave,
            conditions: conditionsToSave,
            actions,
        };
        return newScenario;
    };

    const handleAddScenario = async () => {
        try {
            const scenarioName = name.trim();
            const isNameOk = await checkScenarioName(scenarioName);
            if (!isNameOk) return;

            const scenarioObj = buildScenarioObject();
            if (!scenarioObj) return; // validation failed

            const token = localStorage.getItem('token');
            await axios.post(`${API_URL}/api/scenarios/${workspaceName}`, scenarioObj, {
                headers: { Authorization: `Bearer ${token}` },
            });
            alert('Scenario created successfully!');
        } catch (err) {
            console.error('Error creating scenario:', err);
            alert('Failed to create scenario.');
        }
    };

    const handleReplaceScenario = async () => {
        try {
            const scenarioObj = buildScenarioObject();
            if (!scenarioObj) return;

            const token = localStorage.getItem('token');
            const resp = await axios.get(`${API_URL}/api/scenarios/${workspaceName}?scenarioName=${encodeURIComponent(name)}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const existingScn = resp.data;
            if (!existingScn || !existingScn._id) {
                alert(`Не могу найти сценарий с именем: ${name}`);
                return;
            }

            await axios.put(`${API_URL}/api/scenarios/${workspaceName}/${existingScn._id}`, scenarioObj, {
                headers: { Authorization: `Bearer ${token}` },
            });
            alert('Сценарий заменен (updated) успешно!');
            setNameError('');
            setScenarioExists(false);
        } catch (err) {
            console.error('Error replacing scenario:', err);
            alert('Не могу заменить сценарий.');
        }
    };

    return (
        <div style={{ margin: '20px' }}>
            <h2>Создание или редактирование сценария</h2>

            {/* Scenario Name */}
            <div style={{ marginBottom: '10px' }}>
                <label><strong>Имя сценария: </strong></label>
                <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onBlur={handleNameBlur}
                    style={{ margin: '5px' }}
                />
                {nameError && (
                    <p style={{ color: 'red', margin: 0 }}>
                        {nameError}
                    </p>
                )}
                {scenarioExists && (
                    <button
                        onClick={handleReplaceScenario}
                        style={{
                            background: 'orange',
                            color: '#000',
                            border: 'none',
                            padding: '5px 10px',
                            marginLeft: '10px',
                            cursor: 'pointer',
                        }}
                    >
                        Заменить существующий
                    </button>
                )}
            </div>

            {/* isActive */}
            <label style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}>
                <strong style={{ marginRight: '5px' }}>Активен:</strong>
                <input
                    type="checkbox"
                    checked={isActive}
                    onChange={() => setIsActive(!isActive)}
                    style={{
                        transform: 'scale(1.4)',
                        marginLeft: '5px',
                    }}
                />
            </label>

            <hr />

            {/* TRIGGERS & CONDITIONS combined */}
            <h3>Условия выполнения</h3>
            {triggerConditions.map((item, index) => (
                <div
                    key={index}
                    style={{
                        border: '1px solid #ccc',
                        padding: '10px',
                        marginBottom: '10px',
                    }}
                >
                    <label>Чем запускается:</label>
                    <select
                        value={item.type}
                        onChange={(e) => handleTCChange(index, 'type', e.target.value)}
                        style={{ margin: '0 10px' }}
                    >
                        <option value="sensor">Датчиком</option>
                        <option value="schedule">Расписанием</option>
                        <option value="manual">Вручную</option>
                        {/* NEW: pure schedule-based condition */}
                        {/*<option value="scheduleCondition">Расписанием (условие)</option>*/}
                    </select>




                    {/* Sensor Fields */}
                    {item.type === 'sensor' && (() => {
                        const currentSensor = sensorDevices.find((dev) => dev.name === item.name);
                        const operatorOptions = currentSensor?.operator || [];
                        const outputStates = currentSensor?.outputStates || [];
                        const isAnalogSensor = currentSensor?.isAnalog || false; 

                        return (
                            <>
                                <br />
                                <label>Имя датчика:</label>
                                <select
                                    value={item.name}
                                    onChange={(e) => handleTCChange(index, 'name', e.target.value)}
                                    style={{ margin: '0 10px' }}
                                >
                                    <option value="">-- Выбрать датчик --</option>
                                    {sensorDevices.map((dev) => (
                                        <option key={dev._id} value={dev.name}>
                                            {dev.name}
                                        </option>
                                    ))}
                                </select>
                                <label>Условие:</label>
                                <select
                                    value={item.operator}
                                    onChange={(e) => handleTCChange(index, 'operator', e.target.value)}
                                    style={{ margin: '0 10px' }}
                                >
                                    <option value="">-- Выбрать условие --</option>
                                    {operatorOptions.map((op) => (
                                        <option key={op} value={op}>
                                            {op}
                                        </option>
                                    ))}
                                </select>

                                {/* Dynamically render input or select based on sensor type */}
                                <label>Значение:</label>
                                {isAnalogSensor ? (
                                    <input
                                        type="number"
                                        value={item.value || ''}
                                        onChange={(e) => handleTCChange(index, 'value', e.target.value)}
                                        placeholder="Введите значение (например, 24)"
                                        className="form-control"
                                        style={{ margin: '0 10px' }}
                                    />
                                ) : (
                                    <select
                                        value={item.value}
                                        onChange={(e) => handleTCChange(index, 'value', e.target.value)}
                                        className="form-control"
                                        style={{ margin: '0 10px' }}
                                    >
                                        <option value="">-- Состояние датчика --</option>
                                        {outputStates.map((state) => (
                                            <option key={state} value={state}>
                                                {state}
                                            </option>
                                        ))}
                                    </select>
                                )}

                                <label>Связь со следующим условием:</label>
                                <select
                                    value={item.logicOp || 'AND'}
                                    onChange={(e) => handleTCChange(index, 'logicOp', e.target.value)}
                                    style={{ margin: '0 10px' }}
                                >
                                    <option value="AND">И</option>
                                    <option value="OR">ИЛИ</option>
                                </select>

                                {/* Checkbox to enable schedule per sensor */}
                                <div
                                    style={{
                                        marginLeft: '10px',
                                        padding: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <label
                                        style={{
                                            display: 'flex',
                                            alignItems: 'left',
                                            margin: 0,
                                            padding: 0,
                                        }}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={!!item.useSensorSchedule}
                                            onChange={(e) =>
                                                handleTCChange(index, 'useSensorSchedule', e.target.checked)
                                            }
                                            style={{
                                                display: 'flex',
                                                margin: 0,
                                            }}
                                        />
                                        <span style={{ whiteSpace: 'nowrap', marginLeft: '5px' }}>
                                            По расписанию
                                        </span>
                                    </label>
                                </div>
                                {item.useSensorSchedule && (
                                    <CustomScheduleBuilder
                                        schedule={
                                            item.schedule || {
                                                daysOfWeek: [],
                                                fromHour: 8,
                                                fromMinute: 0,
                                                untilHour: 22,
                                                untilMinute: 0,
                                                excludedDates: []
                                            }
                                        }
                                        onChange={(newSched) => handleScheduleChange(index, newSched)}
                                    />
                                )}
                            </>
                        );
                    })()}

                    {/* Schedule Trigger Fields (unchanged) */}
                    {item.type === 'schedule' && (
                        <>
                            <label>Связь со следующим условием:</label>
                            <select
                                value={item.logicOp || 'AND'}
                                onChange={(e) => handleTCChange(index, 'logicOp', e.target.value)}
                                style={{ margin: '0 10px' }}
                            >
                                <option value="AND">AND</option>
                                <option value="OR">OR</option>
                            </select>
                        <CustomScheduleBuilder
                            schedule={item.schedule || {
                                daysOfWeek: [],
                                fromHour: 8,
                                fromMinute: 0,
                                untilHour: 22,
                                untilMinute: 0,
                                excludedDates: [],
                            }}
                            onChange={(newSched) => {
                                const newArr = [...triggerConditions];
                                newArr[index].schedule = newSched;
                                setTriggerConditions(newArr);
                            }}
                            />
                        </>
                    )}

                    

                    {/* Manual Fields */}
                    {item.type === 'manual' && (
                        <p style={{ marginTop: '10px' }}>
                            Trigger will be activated manually (no extra fields).
                        </p>
                    )}

                    <button
                        onClick={() => handleRemoveTriggerCondition(index)}
                        style={{
                            marginTop: '10px',
                            backgroundColor: 'brown',
                            color: '#fff',
                            border: 'none',
                            padding: '3px 8px',
                            cursor: 'pointer',
                        }}
                    >
                        Удалить
                    </button>
                </div>
            ))}
            <button
                onClick={handleAddTriggerCondition}
                style={{
                    marginBottom: '15px',
                    backgroundColor: '#2196f3',
                    color: '#fff',
                    border: 'none',
                    padding: '5px 10px',
                    cursor: 'pointer',
                }}
            >
                + Добавить еще одно условие
            </button>

            <hr />

            {/* ACTIONS */}
            <h3>Что выполнить</h3>
            {actions.map((action, index) => {
                const errorObj = actionErrors[index] || { name: false, command: false };
                return (
                    <div
                        key={index}
                        style={{
                            border: '1px solid #ccc',
                            padding: '10px',
                            marginBottom: '10px',
                        }}
                    >
                        <label>Тип исполнителя: </label>
                        <select
                            value={action.type}
                            onChange={(e) => handleActionChange(index, 'type', e.target.value)}
                            style={{ marginLeft: '5px', marginRight: '10px' }}
                        >
                            <option value="actuator">Актуатор</option>
                            <option value="alarm">Сигнал</option>
                            <option value="message">Сообщение</option>
                        </select>

                        {action.type === 'actuator' && (() => {
                            const currentActuator = actuatorDevices.find((dev) => dev.name === action.name);
                            const commandOptions = currentActuator?.outputStates || [];

                            return (
                                <>
                                    <label>Имя устройства: </label>
                                    <select
                                        value={action.name}
                                        onChange={(e) => handleActionChange(index, 'name', e.target.value)}
                                        style={{
                                            marginRight: '10px',
                                            borderColor: errorObj.name ? 'red' : '#ccc',
                                        }}
                                    >
                                        <option value="">-- Выбрать --</option>
                                        {actuatorDevices.map((dev) => (
                                            <option key={dev._id} value={dev.name}>
                                                {dev.name}
                                            </option>
                                        ))}
                                    </select>

                                    <label>Команда: </label>
                                    <select
                                        value={action.command}
                                        onChange={(e) => handleActionChange(index, 'command', e.target.value)}
                                        style={{
                                            marginRight: '5px',
                                            borderColor: errorObj.command ? 'red' : '#ccc',
                                        }}
                                    >
                                        <option value="">-- Выбрать команду --</option>
                                        {commandOptions.map((cmd) => (
                                            <option key={cmd} value={cmd}>
                                                {cmd}
                                            </option>
                                        ))}
                                    </select>
                                </>
                            );
                        })()}

                        {action.type === 'alarm' && (
                            <>
                                <label>Тип сигнала:</label>
                                <input
                                    type="text"
                                    value={action.name}
                                    onChange={(e) => handleActionChange(index, 'name', e.target.value)}
                                    style={{
                                        margin: '0 10px',
                                        borderColor: errorObj.name ? 'red' : '#ccc',
                                    }}
                                />
                                <label>Команда:</label>
                                <input
                                    type="text"
                                    value={action.command}
                                    onChange={(e) => handleActionChange(index, 'command', e.target.value)}
                                    style={{
                                        margin: '0 10px',
                                        borderColor: errorObj.command ? 'red' : '#ccc',
                                    }}
                                />
                            </>
                        )}

                        {action.type === 'message' && (
                            <>
                                <label>Message Title (Name):</label>
                                <input
                                    type="text"
                                    value={action.name}
                                    onChange={(e) => handleActionChange(index, 'name', e.target.value)}
                                    style={{
                                        margin: '0 10px',
                                        borderColor: errorObj.name ? 'red' : '#ccc',
                                    }}
                                />
                                <label>Message Content (Command):</label>
                                <textarea
                                    value={action.command}
                                    onChange={(e) => handleActionChange(index, 'command', e.target.value)}
                                    style={{
                                        margin: '0 10px',
                                        borderColor: errorObj.command ? 'red' : '#ccc',
                                    }}
                                />
                            </>
                        )}

                        <button
                            onClick={() => handleRemoveAction(index)}
                            style={{
                                backgroundColor: 'brown',
                                color: '#fff',
                                border: 'none',
                                padding: '3px 8px',
                                cursor: 'pointer',
                            }}
                        >
                            Удалить
                        </button>
                    </div>
                );
            })}
            <button
                onClick={handleAddAction}
                style={{
                    marginBottom: '15px',
                    backgroundColor: '#2196f3',
                    color: '#fff',
                    border: 'none',
                    padding: '5px 10px',
                    cursor: 'pointer',
                }}
            >
                + Добавить исполнителя
            </button>

            <hr />

            {/* SAVE BUTTON */}
            <button
                onClick={handleAddScenario}
                disabled={checkingName}
                style={{
                    marginTop: '15px',
                    padding: '5px 10px',
                    background: checkingName ? '#999' : '#4CAF50',
                    color: '#fff',
                    border: 'none',
                    cursor: 'pointer',
                    borderRadius: '4px',
                }}
            >
                {checkingName ? 'Проверка...' : 'Сохранить сценарий'}
            </button>
        </div>
    );
}

export default AddScenarioPage;
